/**
 * Status- und Beauftragungs-Badges des Mitarbeiterverzeichnisses (G-3/G-9).
 *
 * Status-Farblogik: ausgeschieden rot > krank gelb > Urlaub blau > aktiv gruen.
 */

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import type { AssignmentStatusValue, EmploymentStatus } from "@/types"

interface StatusFields {
  employmentStatus: EmploymentStatus
  isSick: boolean
  isOnVacation: boolean
}

export type EmployeeStatusKey = "left" | "sick" | "vacation" | "active"

function employeeStatusKey(e: StatusFields): EmployeeStatusKey {
  if (e.employmentStatus === "left") return "left"
  if (e.isSick) return "sick"
  if (e.isOnVacation) return "vacation"
  return "active"
}

const STATUS_STYLE: Record<EmployeeStatusKey, { label: string; className: string }> = {
  active: { label: "Aktiv", className: "border-green-300 bg-green-100 text-green-800" },
  sick: { label: "Krank", className: "border-yellow-300 bg-yellow-100 text-yellow-800" },
  vacation: { label: "Urlaub", className: "border-blue-300 bg-blue-100 text-blue-800" },
  left: { label: "Ausgeschieden", className: "border-red-300 bg-red-100 text-red-800" },
}

export function EmployeeStatusBadge({ employee }: { employee: StatusFields }) {
  const s = STATUS_STYLE[employeeStatusKey(employee)]
  return (
    <Badge variant="outline" className={cn("whitespace-nowrap", s.className)}>
      {s.label}
    </Badge>
  )
}

/** Beauftragungs-Ampel 📓 (keine) / 📒 (unvollstaendig/abgelaufen) / 📗 (alle gueltig) */
const ASSIGNMENT_STYLE: Record<
  AssignmentStatusValue,
  { icon: string; label: string; className: string }
> = {
  none: {
    icon: "📓",
    label: "Keine Beauftragungen",
    className: "border-gray-300 bg-gray-100 text-gray-700",
  },
  incomplete: {
    icon: "📒",
    label: "Unvollständig",
    className: "border-yellow-300 bg-yellow-100 text-yellow-800",
  },
  expired_some: {
    icon: "📒",
    label: "Teilweise abgelaufen",
    className: "border-yellow-300 bg-yellow-100 text-yellow-800",
  },
  all_valid: {
    icon: "📗",
    label: "Alle gültig",
    className: "border-green-300 bg-green-100 text-green-800",
  },
}

export function AssignmentBadge({ status }: { status: AssignmentStatusValue }) {
  const s = ASSIGNMENT_STYLE[status]
  return (
    <Badge variant="outline" className={cn("whitespace-nowrap gap-1", s.className)} title={s.label}>
      <span aria-hidden>{s.icon}</span>
      {s.label}
    </Badge>
  )
}
